import { SensorDataSessionItemList, SensorDataSessionMarker, SensorDataSessionMarkerDot } from "@shared/models";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  PaginatedResponse,
  ActionWithPayload,
  Filter,
  WithCallback,
  SensorDataSessionValuesFilter,
  SensorDataSessionMarkerFilter,
  SensorDataSessionProcessedValuesFilter,
} from "@shared/interfaces";
import { hideOverlay, navigate, startLoading, stopLoading } from "@shared/store/actions";
import { downLoadFileFromUrl, getPercentageFromFileUploading, tokenHandler } from "@shared/utils";
import { SensorDataSession } from "@shared/models/SensorDataSession";
import { API, getSensorDataSessionPageURL, getSensorDataSessionsPageURL } from "@shared/constants";

import {
  createSensorDataSession,
  createSensorDataSessionMarker,
  createSensorDataSessionMarkerDot,
  deleteSensorDataSession,
  getSensorDataSession,
  getSensorDataSessionMarkers,
  getSensorDataSessions,
  getSensorDataSessionValues,
  updateSensorDataSession,
  getSensorDataSessionDots,
  deleteSensorDataSessionDot,
  deleteSensorDataSessionMarker,
  getSensorDataSessionProcessedValues,
  downloadSensorDataSessionValues,
} from "./actions";
import api from "../api";
import {
  DeleteSensorDataSessionSuccess,
  CreateUpdateSensorDataSessionSuccess,
  SensorDataSessionValues,
  CreateSensorDataSessionMarkerDto,
  CreateSensorDataSessionMarkerSuccess,
  CreateSensorDataSessionMarkerDotDto,
  CreateSensorDataSessionMarkerDotSuccess,
  DeleteSensorDataSessionMarkerDotDto,
  DeleteSensorDataSessionMarkerDotSuccess,
  DeleteSensorDataSessionMarkerDto,
  DeleteSensorDataSessionMarkerSuccess,
  SensorDataSessionProcessedValues,
  DownloadSensorDataSessionValuesDto,
} from "../interfaces";

function* getSensorDataSessionsSaga({ payload }: ActionWithPayload<Filter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<SensorDataSessionItemList> = yield call(api.getSensorDataSessions, payload);
    yield put(stopLoading());
    yield put(
      getSensorDataSessions.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getSensorDataSessions.failure(error as Error));
    yield put(stopLoading());
  }
}

function* createSensorDataSessionSaga({ payload }: ActionWithPayload<WithCallback<{ data: FormData }>>) {
  try {
    const { cb, data } = payload;
    yield put(startLoading());
    const response: CreateUpdateSensorDataSessionSuccess = yield call(
      api.createSensorDataSession,
      data,
      function getPercentage(progressEvent: { loaded: number; total: number }) {
        const percentage = getPercentageFromFileUploading(progressEvent);
        cb && cb(percentage);
      },
    );

    yield put(hideOverlay());
    yield put(navigate(getSensorDataSessionPageURL(response.session.id)));

    yield put(stopLoading());
    yield put(createSensorDataSession.success(response));
  } catch (error) {
    yield put(createSensorDataSession.failure(error as Error));
    yield put(stopLoading());
  }
}

function* updateSensorDataSessionSaga({ payload }: ActionWithPayload<WithCallback<{ data: FormData; id: number }>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: CreateUpdateSensorDataSessionSuccess = yield call(
      api.updateSensor,
      rest,
      function getPercentage(progressEvent: { loaded: number; total: number }) {
        const percentage = getPercentageFromFileUploading(progressEvent);
        cb && cb(percentage);
      },
    );
    yield put(stopLoading());
    yield put(updateSensorDataSession.success(response));
    yield put(hideOverlay());
  } catch (error) {
    yield put(updateSensorDataSession.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getSensorDataSessionSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: SensorDataSession = yield call(api.getSensorDataSession, payload);
    yield put(stopLoading());
    yield put(getSensorDataSession.success(response));
  } catch (error) {
    yield put(getSensorDataSession.failure(error as Error));
    yield put(stopLoading());
  }
}

function* deleteSensorDataSessionSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: DeleteSensorDataSessionSuccess = yield call(api.deleteSensorDataSession, payload);
    yield put(stopLoading());
    yield put(deleteSensorDataSession.success(response));
    yield put(hideOverlay());
    yield put(navigate(getSensorDataSessionsPageURL()));
  } catch (error) {
    yield put(deleteSensorDataSession.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getSensorDataSessionValuesSaga({
  payload,
}: ActionWithPayload<SensorDataSessionValuesFilter & { id: number }>) {
  try {
    yield put(startLoading());
    const response: SensorDataSessionValues[] = yield call(api.getSensorDataSessionValues, payload);
    yield put(stopLoading());
    yield put(getSensorDataSessionValues.success(response));
  } catch (error) {
    yield put(getSensorDataSessionValues.failure(error as Error));
    yield put(stopLoading());
  }
}

function* downloadSensorDataSessionValuesSaga({ payload }: ActionWithPayload<DownloadSensorDataSessionValuesDto>) {
  try {
    yield put(startLoading());

    const queryString = new URLSearchParams({
      auth_key: String(tokenHandler.get()),
      start: payload.start,
      end: payload.end,
    }).toString();

    const baseURL = API.SENSOR_DATA_SESSIONS.DOWNLOAD_SENSOR_DATA_SESSION_VALUES(payload.session_id);
    const URL = `${baseURL}?${queryString}`;

    downLoadFileFromUrl(URL);
    yield put(stopLoading());
  } catch (error) {
    yield put(downloadSensorDataSessionValues.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getSensorDataSessionProcessedValuesSaga({
  payload,
}: ActionWithPayload<SensorDataSessionProcessedValuesFilter>) {
  try {
    yield put(startLoading());
    const response: SensorDataSessionProcessedValues[] = yield call(api.getSensorDataSessionProcessedValues, payload);
    yield put(stopLoading());
    yield put(getSensorDataSessionProcessedValues.success(response));
  } catch (error) {
    yield put(getSensorDataSessionProcessedValues.failure(error as Error));
    yield put(stopLoading());
  }
}

function* createSensorDataSessionMarkerSaga({ payload }: ActionWithPayload<CreateSensorDataSessionMarkerDto>) {
  try {
    yield put(startLoading());
    const response: CreateSensorDataSessionMarkerSuccess = yield call(api.createSensorDataSessionMarker, payload);
    yield put(stopLoading());
    yield put(createSensorDataSessionMarker.success(response));
    yield put(hideOverlay());
  } catch (error) {
    yield put(createSensorDataSessionMarker.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getSensorDataSessionMarkersSaga({ payload }: ActionWithPayload<SensorDataSessionMarkerFilter>) {
  try {
    yield put(startLoading());
    const response: SensorDataSessionMarker[] = yield call(api.getSensorDataSessionMarkers, payload);
    yield put(stopLoading());
    yield put(getSensorDataSessionMarkers.success(response));
    yield put(hideOverlay());
  } catch (error) {
    yield put(getSensorDataSessionMarkers.failure(error as Error));
    yield put(stopLoading());
  }
}

function* createSensorDataSessionMarkerDotSaga({
  payload,
}: ActionWithPayload<WithCallback<CreateSensorDataSessionMarkerDotDto>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: CreateSensorDataSessionMarkerDotSuccess = yield call(api.createSensorDataSessionMarkerDot, rest);
    yield put(stopLoading());
    yield put(createSensorDataSessionMarkerDot.success(response));
    cb && cb();
    yield put(hideOverlay());
  } catch (error) {
    yield put(createSensorDataSessionMarkerDot.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getSensorDataSessionDotsSaga({
  payload,
}: ActionWithPayload<{ id: number; files: number[]; station: string }>) {
  try {
    yield put(startLoading());
    const response: SensorDataSessionMarkerDot[] = yield call(api.getSensorDataSessionDots, payload);
    yield put(stopLoading());
    yield put(getSensorDataSessionDots.success(response));
    yield put(hideOverlay());
  } catch (error) {
    yield put(getSensorDataSessionDots.failure(error as Error));
    yield put(stopLoading());
  }
}

function* deleteSensorDataSessionDotSaga({ payload }: ActionWithPayload<DeleteSensorDataSessionMarkerDotDto>) {
  try {
    yield put(startLoading());
    const response: DeleteSensorDataSessionMarkerDotSuccess = yield call(api.deleteSensorDataSessionMarkerDot, payload);
    yield put(stopLoading());
    yield put(deleteSensorDataSessionDot.success(response));
    yield put(hideOverlay());
  } catch (error) {
    yield put(deleteSensorDataSessionDot.failure(error as Error));
    yield put(stopLoading());
  }
}

function* deleteSensorDataSessionMarkerSaga({ payload }: ActionWithPayload<DeleteSensorDataSessionMarkerDto>) {
  try {
    yield put(startLoading());
    const response: DeleteSensorDataSessionMarkerSuccess = yield call(api.deleteSensorDataSessionMarker, payload);
    yield put(stopLoading());
    yield put(deleteSensorDataSessionMarker.success(response));
    yield put(hideOverlay());
  } catch (error) {
    yield put(deleteSensorDataSessionMarker.failure(error as Error));
    yield put(stopLoading());
  }
}

function* sensorDataSessionSaga() {
  yield takeLatest(getSensorDataSessions.request, getSensorDataSessionsSaga);
  yield takeLatest(createSensorDataSession.request, createSensorDataSessionSaga);
  yield takeLatest(getSensorDataSession.request, getSensorDataSessionSaga);
  yield takeLatest(deleteSensorDataSession.request, deleteSensorDataSessionSaga);
  yield takeLatest(updateSensorDataSession.request, updateSensorDataSessionSaga);
  yield takeLatest(getSensorDataSessionValues.request, getSensorDataSessionValuesSaga);
  yield takeLatest(getSensorDataSessionProcessedValues.request, getSensorDataSessionProcessedValuesSaga);
  yield takeLatest(createSensorDataSessionMarker.request, createSensorDataSessionMarkerSaga);
  yield takeLatest(getSensorDataSessionMarkers.request, getSensorDataSessionMarkersSaga);
  yield takeLatest(createSensorDataSessionMarkerDot.request, createSensorDataSessionMarkerDotSaga);
  yield takeLatest(getSensorDataSessionDots.request, getSensorDataSessionDotsSaga);
  yield takeLatest(deleteSensorDataSessionDot.request, deleteSensorDataSessionDotSaga);
  yield takeLatest(deleteSensorDataSessionMarker.request, deleteSensorDataSessionMarkerSaga);
  yield takeLatest(downloadSensorDataSessionValues.request, downloadSensorDataSessionValuesSaga);
}

export default sensorDataSessionSaga;
